(ns app.front.root
  (:require ["@carbon/icons-react/es/calendar/24" :default Calendar24]
            ["@carbon/icons-react/es/user--favorite--alt/24" :default UserFavoriteAlt24]
            [app.domain.branding.model :as branding-model]
            [app.domain.group.model :as group-model]
            [app.front.branding]
            [app.front.dashboard.group-configs :as dashboard-configs]
            [app.front.process]
            [app.front.requirement.components :as requirement]
            [app.front.ui :as ui]
            [app.front.ui.call-to-action :as call-to-action]
            [app.front.ui.chat-panel :as chat-panel]
            [app.front.ui.contact-request-panel :as contact-request]
            [app.front.ui.dropdown :as ui-dd]
            [app.front.ui.messages-panel :as messages-panel]
            [app.front.ui.options-panel :as options-panel]
            [app.front.ui.service-hub-panel :as service-hub-panel]
            [app.front.ui.sub-header :as sub-header]
            [app.front.ui.tasks-panel :as tasks-panel]
            [app.front.ui.top-bar :as top-bar]
            [app.front.ui.viewport-observer :as viewport-observer]
            [app.front.ui.visual-viewport :as visual-viewport]
            [app.front.util :refer [for-all for-frag]]
            [app.front.view.admin.common :as acommon]
            [app.front.view.conditions.comments :as conditions-comments]
            [app.front.view.conversations.core :as conversation]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.login :as login]
            [app.front.view.navigation :as nav]
            [app.front.view.not-found :as not-found]
            [app.front.view.registration.terms-of-service :as tos]
            [app.front.view.whats-coming :as whats-coming]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.i18n :as i18n]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [core.front.session :as session]
            [core.user.model :as user-model]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-db ::toggle-group-selector-open
  (fn [db _]
    (update db ::group-selector-open? not)))

(rf/reg-event-db ::set-group-selector-open
  (fn [db [_ open?]]
    (assoc db ::group-selector-open? open?)))

(rf/reg-sub ::group-selector-open?
  (fn [db _]
    (get db ::group-selector-open? false)))

;; Briefly highlights the "What's coming" header CTA after the modal is closed
;; (WIN-5563). The flag is cleared once the CSS animation (2000ms) has finished
;; so the highlight can be replayed on the next close.
(rf/reg-sub ::whats-coming-cta-flashing?
  (fn [db _]
    (get db ::whats-coming-cta-flashing? false)))

(rf/reg-event-fx ::flash-whats-coming-cta
  (fn [{:keys [db]} _]
    {:db             (assoc db ::whats-coming-cta-flashing? true)
     :dispatch-later [{:ms 2000 :dispatch [::clear-whats-coming-cta-flash]}]}))

(rf/reg-event-db ::clear-whats-coming-cta-flash
  (fn [db _]
    (dissoc db ::whats-coming-cta-flashing?)))

(rf/reg-event-fx ::fetch-conversation-notification-count-immediately
  (fn [_ [_ refresh? workspace-id]]
    {:comm/command (merge {:command    :conversation/get-conversation-notification-count
                           :method     :get
                           :on-success [::set-conversation-notification-count refresh? workspace-id]}
                          (when workspace-id
                            {:data {:workspace-id workspace-id}}))}))

(rf/reg-event-fx ::fetch-conversation-notification-count
  (fn [{:keys [db]} [_ refresh?]]
    {:dispatch-debounce {:id      ::fetch-conversation-notification-count
                         :timeout 300
                         :action  :dispatch
                         :event   [::fetch-conversation-notification-count-immediately refresh? (some-> db :view/workspace :workspace/id)]}}))

(rf/reg-event-fx ::set-conversation-notification-count
  (fn [{{previous-count ::conversation-notification-count
         :as            db}                               :db}
       [_ refresh? workspace-id status {:keys [conversation-notification-count]}]]
    (when (= status 200)
      (cond-> {:db (assoc db
                          (if workspace-id
                            ::ws-conversation-notification-count
                            ::conversation-notification-count)
                          conversation-notification-count)}
        (and refresh? (not= conversation-notification-count previous-count))
        (assoc :dispatch [:app.front.view.conversations.core/refresh-thread])))))

(rf/reg-sub ::all-conversation-notification-count
  #(get % ::conversation-notification-count))

(rf/reg-sub ::ws-conversation-notification-count
  #(get % ::ws-conversation-notification-count))

(rf/reg-sub ::conversation-notification-count
  :<- [::ws-subs/workspace-id]
  :<- [::all-conversation-notification-count]
  :<- [::ws-conversation-notification-count]
  (fn [[ws all-count ws-count] _]
    (if ws
      ws-count
      all-count)))

(rf/reg-sub ::admin-permissions
  :<- [:session/user]
  :-> :user/admin-permissions)

(rf/reg-sub ::superdomain-groups
  :<- [:session/user]
  (fn [user _]
    (->> user
         :user/superdomain-groups
         (remove (comp empty? :management-views))
         (sort-by :group/name))))

(rf/reg-sub ::superdomain-navigable-groups
  :<- [:session/user]
  (fn [user _]
    (let [superdomains     (:user/superdomain-groups user)
          ;; Only include groups that come via superdomain, group by parent
          groups-by-parent (fn [attr-key user]
                             (->> user
                                  attr-key
                                  (filter :via-superdomain?)
                                  (group-by :entity/parent)))
          domains          (groups-by-parent :user/domain-groups user)
          authorities      (groups-by-parent :user/authority-groups user)
          ;; Get children of a parent, sorted by name
          sorted-children  (fn [parent-id children-by-parent]
                             (->> (get children-by-parent parent-id)
                                  (sort-by :group/name)))]
      ;; Build hierarchical list in order:
      ;; superdomain -> domains -> authorities
      ;; (but without the superdomains themselves, as their management views are displayed directly)
      (->> superdomains
           (sort-by :group/name)
           (mapcat
             (fn [superdomain]
               (let [child-domains (sorted-children (:db/id superdomain) domains)]
                 (mapcat
                   (fn [domain]
                     (let [child-authorities (sorted-children (:db/id domain) authorities)]
                       (cons domain child-authorities)))
                   child-domains))))
           ;; Drop groups we don't actually have any management rights to
           (remove (comp empty? :management-views))))))

(rf/reg-sub ::admin-views
  :<- [:tr]
  :<- [::admin-permissions]
  :<- [::ff/enabled? :feature-flag.id/batch-group-creation]
  :<- [::ff/enabled? :feature-flag.id/new-tenant-management]
  (fn [[tr permissions batch-enabled? tenant-management-enabled?] _]
    (->> (concat
           (when (:permission/admin permissions)
             (concat [:view/admin.audit-log
                      :view/admin.entity-lookup
                      :view/admin.signal-debugger
                      :view/admin.commands
                      :view/admin.export-import
                      :view/admin.gis-configs
                      :view/admin.integration-messages
                      :view/admin.municipality-groups
                      :view/admin.upper-tier-municipality-groups
                      :view/admin.approver-groups
                      :view/admin.users
                      :view/admin.oidc-configs
                      :view/admin.excise
                      :view/admin.ws-export
                      :view/admin.metrics
                      :view/admin.feature-flags
                      :view/admin.api-keys
                      :view/admin.superdomain]
                     (when batch-enabled?
                       [:view/admin.batch-group-creation])
                     (when tenant-management-enabled?
                       [:view/admin.tenant-management])))
           (when (some #{:permission.admin/reporting
                         :permission/admin}
                       permissions)
             [:view/admin.reporting]))
         (map (juxt identity #(tr [%])))
         (sort-by last)
         seq)))

(rf/reg-sub ::selected-lang
  #(get % :language))

(rf/reg-sub ::build-info
  :<- [::front-config/config]
  :-> :build-info)

(rf/reg-sub ::permit-types-tr
  :<- [:tr]
  (fn [tr [_ permit-types]]
    (-> (i18n-model/get-permit-type-specific-tr-keys permit-types)
        (i18n-model/wrap-tr-fn tr))))

(defn header-button [{:keys [text icon icon-description href on-click dropdown? blank? class badge inline? carbon-icon]}]
  [(if (nil? href) :div :a)
   (apply merge
          {:class
           ["cloudpermit--header--button relative flex flex-row self-stretch items-center px-2 md:px-4 shadow-none no-underline header-text gap-1 md:gap-2"
            (when-not dropdown? "header-hover")
            (when-not href "cursor-pointer")
            class]}
          (when href {:aria-label text})
          (when-not dropdown? [(p/assoc-if {}
                                           :on-click on-click
                                           :href href)
                               (when blank? {:rel "noopener" :target "_blank"})]))
   (cond
     carbon-icon [:span.md.mr-2.mr-1
                  [:> carbon-icon]]
     icon (cc/icon (p/assoc-if {:icon        icon
                                :description (or icon-description text)
                                :fill        "currentColor"}
                               :aria-hidden (when (not icon-description) true))))
   [:span.whitespace-nowrap
    {:class       (if (or inline? (not icon)) "inline" (if dropdown? "hidden md:inline" "hidden lg:inline"))
     :aria-hidden true}
    text]
   (when badge [:span.cloudpermit--notification-badge
                {:style {:top "50%" :left "3em" :margin-top "-2.5em"}}
                badge])])

(defn header-dropdown [params & children]
  (into [ui-dd/dropdown
         (p/assoc-if {:class         ["flex flex-row self-stretch items-stretch" (:class params)]
                      :toggle-props  {:class "header-hover cloudpermit--header--button header-text flex flex-row self-stretch items-center shadow-none no-underline pr-2 md:pr-4"}
                      :options-props {:class "header-color header-text overflow-auto"
                                      :style (merge {:max-height "calc(100vh - 80px - var(--env-banner-offset, 0px))"}
                                                    (:options-props-style params))}}
                     :data-cy (:data-cy params)
                     :open-icon-description (:open-icon-description params)
                     :close-icon-description (:close-icon-description params)
                     :on-toggle (:on-toggle params))
         [header-button (assoc (dissoc params :open-icon-description :close-icon-description :options-props-style) :dropdown? true)]]
        children))

(defn header-dropdown-title [text]
  [:div.header-dropdown-title.flex.flex-row.self-stretch.items-center.justify-end.mx-4.pt-4.pb-2.mt-4.text-xs.border-t.border-solid
   [:span text]])

(defn header-dropdown-item [{:keys [text icon href on-click class blank? left? data-cy]}]
  (cc/link
    (merge
      (-> {:class-name (str/join " "
                                 ["cloudpermit--header--button"
                                  "header-text"
                                  "header-hover"
                                  "flex"
                                  "flex-row"
                                  "self-stretch"
                                  "items-center"
                                  "px-4"
                                  "py-4"
                                  "no-underline"
                                  "hover:shadow-none"
                                  "sm:py-2"
                                  class
                                  (when (not left?) "justify-end")])}
          (p/assoc-if :data-cy data-cy))
      (if on-click {:href "#" :on-click on-click} {:href href})
      (when blank? {:rel "noopener" :target "_blank"}))
    [:span.whitespace-nowrap text]
    (when icon (cc/icon {:icon        icon
                         :width       "14"
                         :height      "14"
                         :description text
                         :fill        (-> (<sub [:branding/config])
                                          :group/branding-banner-fg-color
                                          (or "#ffffff"))
                         :class-name  "ml-2 header-logo"
                         :aria-hidden true}))))

(defn admin-dropdown-items [user tr]
  (let [authority-groups   (->> user :user/authority-groups (remove :via-superdomain?))
        domain-groups      (->> user :user/domain-groups (remove :via-superdomain?))
        superdomain-groups (<sub [::superdomain-groups])
        other-groups       (-> user :user/administered-groups)
        ;; Combine all groups and remove duplicates by :db/id
        all-groups         (->> (concat superdomain-groups authority-groups domain-groups other-groups)
                                (util/distinct-by :db/id))]
    [:<>
     (when-let [admin-views (<sub [::admin-views])]
       [:<>
        [header-dropdown-title (tr [:admin-tools/title])]
        (for-frag [[view text] admin-views]
          [header-dropdown-item {:key  view
                                 :text text
                                 :href (href/href view)}])])
     (for-frag [{:keys [management-views] :as group} all-groups
                :when (seq management-views)
                :let [tr (<sub [::permit-types-tr (group-model/get-scope-permit-types group)])]]
       [:<>
        [header-dropdown-title (str (:group/name group))]
        (for-frag [[k text] (->> management-views
                                 (map #(vector % (tr [%])))
                                 (sort-by last))]
          [header-dropdown-item {:key     (str (:group/name group) "-" (name k))
                                 :data-cy (:group/id group)
                                 :text    text
                                 :href    (href/href k {:group-id (:db/id group)})}])])]))

(defn system-admin-group-management-items [tr route-name]
  [:div.cloudpermit--header--admin-group-opts
   (let [available-groups (<sub [::acommon/groups-for-dropdown])
         cur-group-id     (<sub [::gas/group-id])
         current-group    (<sub [::gas/group])
         current-branding (<sub [:branding])
         group-admin-rt?  (gas/group-admin-route? route-name)]
     ;; group-id is available immediately in group context, group takes some time to load
     (when (not= route-name :dashboard)
       [:<>
        [header-dropdown-title (tr [:admin-tools/municipality-group-config])]
        (if (or (not cur-group-id)
                current-group
                ;; Display e.g. in workspace view when there is no group admin group selected
                (not group-admin-rt?))
          (let [allowed-group (when current-group
                                (-> available-groups
                                    (p/find-first {:id (:db/id current-group)})))]
            [:<>
             [:div.flex.mx-4.pb-2.mt-2.cloudpermit--header--group-select
              [dashboard-configs/group-selector
               {:id                    "admin-menu-group-selector"
                :tr                    tr
                :initial-selected-item allowed-group
                :on-change             (fn [{:keys [id]}]
                                         (when (and id group-admin-rt?)
                                           (>evt [:route/push {:route  route-name
                                                               :params {:group-id id}}])))}]]
             (when (and cur-group-id
                        ;; Display views only for allowed (authority) groups or when there is no current group
                        (or allowed-group
                            (nil? current-group)))
               (if-let [admin-views (seq (<sub [::gas/management-views]))]
                 (for-frag [[view text] admin-views]
                   [header-dropdown-item {:key  view
                                          :text text
                                          :href (href/branded-href (branding-model/or-default (or (:uri-path-id allowed-group)
                                                                                                  current-branding))
                                                                   view
                                                                   {:group-id cur-group-id}
                                                                   (when-not (gas/group-admin-route? view)
                                                                     {:group-id cur-group-id}))}])
                 [:div.flex.mx-4.justify-end
                  (cc/inline-loading {:description (tr [:admin-tools/loading-options])})]))])
          [:div.flex.mx-4.justify-end
           (cc/inline-loading {:description (tr [:admin-tools/loading-options])})])]))])

(defn superdomain-user-group-management-items [tr route-name]
  [:div.cloudpermit--header--superdomain-group-opts
   (let [available-groups (<sub [::acommon/groups-for-dropdown [::superdomain-navigable-groups] true])
         cur-group-id     (<sub [::gas/group-id])
         current-group    (<sub [::gas/group])
         group-admin-rt?  (gas/group-admin-route? route-name)]
     [:<>
      [header-dropdown-title (tr [:admin/superdomain-selector])]
      (if (or (not cur-group-id)
              current-group
              ;; Display e.g. in workspace view when there is no group admin group selected
              (not group-admin-rt?))
        (let [allowed-group (when current-group
                              (-> available-groups
                                  (p/find-first {:id (:db/id current-group)})))]
          [:<>
           [:div.flex.mx-4.pb-2.mt-2.cloudpermit--header--group-select
            [dashboard-configs/group-selector
             {:id                    "superdomain-menu-group-selector"
              :tr                    tr
              :skip-sorting?         true
              :groups-subscription   [::superdomain-navigable-groups]
              :initial-selected-item allowed-group
              :on-change             (fn [{:keys [id]}]
                                       (when id
                                         (>evt [::gas/set-group-id-and-route id route-name])))
              :on-toggle-click       #(>evt [::toggle-group-selector-open])}]]
           (when (and cur-group-id
                      (->> current-group group-model/superdomain? not))
             (if-let [admin-views (seq (<sub [::gas/management-views]))]
               (for-frag [[view text] admin-views]
                 [header-dropdown-item {:key  view
                                        :text text
                                        :href (href/href view
                                                         {:group-id cur-group-id}
                                                         (when-not (gas/group-admin-route? view)
                                                           {:group-id cur-group-id}))}])
               [:div.flex.mx-4.justify-end
                (cc/inline-loading {:description (tr [:admin-tools/loading-options])})]))])
        [:div.flex.mx-4.justify-end
         (cc/inline-loading {:description (tr [:admin-tools/loading-options])})])])])

(defn support-button [tr]
  [header-button {:text   (tr [:support-text])
                  :icon   i/circle-question
                  :href   (tr [:support-link])
                  :blank? true
                  :class  "hidden md:flex"}])

(defn whats-coming-button [tr]
  (when (ff/enabled? :feature-flag.id/upcoming-changes-modal)
    (let [flashing? (<sub [::whats-coming-cta-flashing?])]
      [header-button {:text        (tr [:whats-coming/button])
                      :on-click    #(>evt [::whats-coming/open])
                      :carbon-icon Calendar24
                      :class       (str "hidden md:flex"
                                        (when flashing? " animate-whats-coming-highlight"))}])))

(defn language-button [{:keys [tr]}]
  (let [languages (<sub [::i18n/locales])
        selected  (<sub [::selected-lang])]
    (when (> (count languages) 1)
      [header-dropdown {:text                   (get-in languages [selected :short-name] "")
                        :icon                   i/world
                        :icon-description       (tr [:language/language])
                        :open-icon-description  (tr [:open-menu])
                        :close-icon-description (tr [:close-menu])
                        :class                  "flex uppercase"
                        :inline?                true
                        :align                  "left"}
       (for-frag [key (keys languages)]
         [header-dropdown-item {:text     (str (get-in languages [key :name]) " (" (str/upper-case (get-in languages [key :short-name])) ")")
                                :left?    true
                                :on-click #(>evt [::i18n/change-language key])}])])))

(defn user-menu [user logged-in? tr route-name]
  (let [system-admin?                (<sub [:session/admin?])
        superdomain-navigable-groups (<sub [::superdomain-navigable-groups])
        branding                     (<sub [:branding])
        selector-open?               (<sub [::group-selector-open?])]
    [header-dropdown (p/assoc-if {:text                   (user-model/full-name user)
                                  :icon                   i/user
                                  :icon-description       (tr [:user/user])
                                  :open-icon-description  (tr [:open-menu])
                                  :close-icon-description (tr [:close-menu])
                                  :data-cy                "navigation-dropdown"
                                  :class                  "hidden md:flex"
                                  :options-props-style    (when selector-open?
                                                            ;; The superdomain selector will be clipped if user
                                                            ;; has a very short user menu. Ensure it always displays
                                                            ;; properly by growing its parent.
                                                            {:min-height "min(500px, calc(100vh - 100px))"})
                                  :on-toggle              (fn [open?] (when (not open?)
                                                                        (>evt [::set-group-selector-open false])))}
                                 :carbon-icon (when system-admin?
                                                UserFavoriteAlt24))
     [header-dropdown-item {:text (tr [:view/profile])
                            :icon i/user-pen
                            :href (href/href :view/profile)}]
     [header-dropdown-item {:text     (tr [:logout-text])
                            :icon     i/log-out
                            :on-click #(session/logout-post % branding)}]
     (cond
       system-admin?
       [system-admin-group-management-items tr route-name]

       (and (not system-admin?) (seq superdomain-navigable-groups))
       [superdomain-user-group-management-items tr route-name])
     (when logged-in?
       [admin-dropdown-items user tr])]))

(defn mobile-menu [user tr]
  (let [branding (<sub [:branding])]
    [header-dropdown {:text  (tr [:menu])
                      :class "flex md:hidden"}
     [header-dropdown-item {:text (user-model/full-name user)
                            :icon i/user
                            :href (href/href :view/profile)}]
     (when (ff/enabled? :feature-flag.id/upcoming-changes-modal)
       [header-dropdown-item {:text     (tr [:whats-coming/button])
                              :on-click #(>evt [::whats-coming/open])
                              :icon     i/calendar
                              :class    (when (<sub [::whats-coming-cta-flashing?])
                                          "animate-whats-coming-highlight")}])
     [header-dropdown-item {:text   (tr [:support-text])
                            :icon   i/circle-info
                            :href   (tr [:support-link])
                            :blank? true}]
     [header-dropdown-item {:text     (tr [:logout-text])
                            :icon     i/log-out
                            :on-click #(session/logout-post % branding)}]]))

(defn inert-props
  "Produce DOM props map with the inert property set when needed."
  []
  (when (<sub [::conversation/conversation-overlay-visible?])
    {:inert ""}))


(defn powered-by-cloudpermit
  [tr & [small?]]
  [:div.flex.flex-col.p-2
   {:style {:flex-shrink "0"}}
   [:span.uppercase.whitespace-nowrap
    {:style {:font-size      (if small? "8px" "9px")
             :letter-spacing "0.06em"}}
    (tr [:powered-by])]
   [:img.self-start
    {:src   "/asset/img/Cloudpermit_logo_RGB_dark.svg"
     :alt   "Cloudpermit"
     :style {:height     (if small? "18px" "20px")
             :margin-top "2px"}}]])

(defn base-header [tr right-side-content]
  [:header#root-header.header.flex-row.flex.justify-between.items-center.w-full.top-0.header-color.gap-4
   (inert-props)
   (let [superdomain?      (= :group.type/superdomain (<sub [:branding/group-type]))
         branding-logo-url (when (and (<sub [:branding]) (not superdomain?))
                             (<sub [:branding/image-url :logo]))
         branding-config   (when (and (<sub [:branding])
                                      (not superdomain?))
                             (<sub [:branding/config]))
         header-title      (when-let [strings (:group/branding-header-titles branding-config)]
                             (let [lang (<sub [:language])]
                               (or (get strings lang)
                                   (get strings :en)
                                   (some val strings))))]
     [:div.flex.flex-row.flex-1.items-center.min-w-0
      [:a.cloudpermit--header--button.bx--link.px-2.md:px-6.lg:px-8.shadow-none.no-underline
       {:href (href/href :dashboard)
        :id   "logo-dashboard-link"}
       ;; Use the municipality branding logo when available; otherwise fall back to the default Cloudpermit logo
       ;; so there is always a consistent element the user can click to return to the dashboard.
       [:img.header-logo {:src   (or branding-logo-url "/asset/img/Cloudpermit_logo_RGB_white.svg")
                          :alt   (tr [:link-to-dashboard])
                          :style {:height          "70px"
                                  :max-width       "160px"
                                  :object-fit      "contain"
                                  :object-position "left center"
                                  :width           "100%"}}]]
      [:div.flex.flex-row.flex-1.min-w-0.items-center.gap-2
       (when (str-util/blank->nil header-title)
         [:div.hidden.sm:block.leading-normal.font-bold.text-lg.header-text.truncate header-title])
       (when-let [env (top-bar/env-name)]
         (when (not= env "prod")
           [:div.hidden.sm:block.items-center.uppercase
            [ui/tag {:type  :round
                     :size  :small
                     :color :blue}
             [:span.font-bold env]]]))]])
   right-side-content])

(defn header [logged-in? user tr plain-header? route-name]
  [base-header
   tr
   [:<>
    (when (and logged-in?
               (not plain-header?))
      [:div.flex.justify-end.items-end.self-stretch
       [support-button tr]
       [whats-coming-button tr]
       [language-button (util/mmap tr)]
       [user-menu user logged-in? tr route-name]
       [mobile-menu user tr]])]])


(defn main-wrapper [view route-match]
  (let [full-screen-view? (get-in route-match [:data :full-screen-view?])]
    [:main
     {:class (if full-screen-view?
               "flex flex-col flex-1 min-h-0 !p-0 transition-none"
               "bx--grid max h-full")}
     [view route-match]]))

(rf/reg-event-fx
  ::redirect-to-login
  (fn [_ [_ original-path]]
    {:route/push (cond-> {:route :login}
                   original-path (assoc :query {:go original-path}))}))

(defn main [route-match logged-in?]
  (let [{:keys [view own-layout?]} (-> route-match :data)]
    (if (some? view)
      (if (or (-> route-match :data :public?)
              logged-in?)
        (if own-layout?
          [view route-match]
          [main-wrapper view route-match])
        (rf/dispatch-sync [::redirect-to-login (href/match->href route-match)]))
      [main-wrapper not-found/not-found-view])))

(defn footer-impl
  [tr {:keys [git-branch git-tag time build-number]} footer-content]
  (let [not-mobile? (<sub [::media-query/breakpoint-matches? :sm])]
    [:footer.flex.flex-col.justify-end
     [:div.flex.justify-between.items-center.mx-4.sm:mx-8
      {:style {:height 60}}
      (when (<sub [:branding])
        (powered-by-cloudpermit tr (not not-mobile?)))
      [:div footer-content]
      [:button.btn-back-to-top.text-ui-black.mx-4
       {:on-click #(>evt [::nav/scroll-to-top])}
       [:span.mr-2.whitespace-nowrap (tr (if not-mobile?
                                           [:back-top "Back to top"]
                                           [:top "Top"]))]
       (cc/icon {:icon        i/chevron-top
                 :description (tr [:back-top "Back to top"])})]]
     [:div.version-row
      (when time
        (str git-branch
             " "
             (when (str-util/blank->nil git-tag) (str "(" git-tag ") "))
             (date-util/format-technical-date-time time)
             " (" build-number ")"))]]))

(defn footer [tr route-match]
  (let [build-info   (<sub [::build-info])
        public?      (-> route-match :data :public?)
        privacy-link [login/footer-link "?privacy=true" (tr [:login/privacy-notice])]]
    [footer-impl tr build-info (when public? privacy-link)]))

(defn- public-privacy-modal
  [route-match]
  (when (and (-> route-match :data :public?)
             (some-> route-match :query-params :privacy (= "true")))
    [tos/visible-doc-modal :privacy-policy]))

(defmulti notification-child (fn [child-method _] child-method))

(defmethod notification-child :default [_ _] nil)

(defmethod notification-child :navigate-permit [_ {:keys [workspace-id permit-type]}]
  (let [tr (<sub [::permit-types-tr [(or permit-type :permit-type/B)]])]
    [:p.permit-notification
     [:a
      {:href     (str "/workspace/" workspace-id "/application#section--application-permits")
       :on-click #(>evt [::navigate-to-permit workspace-id])}
      (tr [:permit/show-permit])]]))

(defn notifications []
  (let [notifications (<sub [:inline-notifications])
        tr            (<sub [:tr])]
    (when-not (empty? notifications)
      (into
        [:div.notifications-wrapper]
        (for-all [{:keys [id kind title caption hide-close-button
                          child class-name child-method parameters]} notifications]
          (cc/inline-notification
            (merge {:key        id
                    :kind       kind
                    :title      (tr [(or title "")])
                    :subtitle   (tr [caption])
                    :data-cy    "system-inline-notification"
                    :class-name (or class-name "system-inline-notification")}
                   (if hide-close-button
                     {:hide-close-button true}
                     {:on-close-button-click #(>evt [:notifications/remove id])}))
            (cond child child
                  child-method [notification-child child-method parameters])))))))

(defn toast-notifications []
  (let [notifications (<sub [:toast-notifications])
        tr            (<sub [:tr])]
    (when-not (empty? notifications)
      (into
        [:div.fixed-notifications-wrapper]
        (for-all [{:keys [id kind title subtitle caption]} notifications]
          (cc/toast-notification
            {:key                   id
             :kind                  kind
             :title                 (tr [title])
             :subtitle              (or (and subtitle (tr [subtitle])) "")
             :caption               (if (sequential? caption)
                                      ;; Treat as tr args directly
                                      (apply tr caption)
                                      (tr [caption]
                                          (when (and (qualified-keyword? caption)
                                                     (contains? #{"error-with-req-id" "db.error"} (namespace caption)))
                                            (if (and (string? id)
                                                     (not (str/starts-with? id "notification#")))
                                              [id]
                                              (js/console.error "Error caption expected request id but no id was provided.")))))
             :class-name            "system-toast-notification"
             :timeout               (case kind (:error :warning) 0 5000)
             :on-close-button-click #(>evt [:notifications/remove id])}))))))


(defn root-view []
  (if (<sub [:core.front.init/app-loaded?])
    (let [logged-in?              (<sub [:session/logged-in?])
          user                    (<sub [:session/user])
          route-match             (<sub [:route/match])
          full-screen-view?       (get-in route-match [:data :full-screen-view?])
          tr                      (<sub [:tr])
          env                     (top-bar/env-name)
          env-banner?             (contains? top-bar/env-alert-envs env)
          {:keys [hide-footer?
                  hide-header?
                  plain-header?
                  name
                  custom-header
                  public-header]} (-> route-match :data)
          header-view             (cond
                                    custom-header
                                    [custom-header tr]

                                    (and public-header (not logged-in?))
                                    [public-header tr]

                                    (not hide-header?)
                                    [header logged-in? user tr plain-header? name])
          side-panel-layout?      (or custom-header (not hide-header?))
          chat-enabled?           (<sub [::chat-panel/chat-enabled?])
          header-area             [:<>
                                   (when (or custom-header (not hide-header?))
                                     [sub-header/sub-header tr route-match])
                                   [notifications]]
          side-panels             [:<>
                                   (when (ff/enabled? :feature-flag.id/service-hub-panel)
                                     [service-hub-panel/service-hub-panel tr])
                                   [options-panel/options-panel tr]
                                   [messages-panel/messages-panel tr]
                                   [tasks-panel/tasks-panel tr]
                                   (when chat-enabled?
                                     [chat-panel/chat-side-panel])
                                   [contact-request/contact-request-panel tr]
                                   [call-to-action/call-to-action-panel]]]
      [:div.wrapper
       {:class [(when ^boolean js/goog.DEBUG "debug-screens")
                (when full-screen-view? "fixed inset-0 flex flex-col overflow-hidden")]
        :style (merge (when env-banner?
                        {"--env-banner-offset" "44px"})
                      (when full-screen-view? {:height "100dvh" :min-height 0}))}
       (when (some->> env (contains? top-bar/env-alert-envs))
         [top-bar/environment-banner tr env])
       (if full-screen-view? [:div.shrink-0 header-view] header-view)
       [:div#root-sticky-header.sticky.top-0
        {:class ["z-[8996] focus-within:z-[8997]" (when full-screen-view? "shrink-0")]
         :style {:top "var(--env-banner-offset, 0px)"}}
        header-area]
       [viewport-observer/observer {:id :sticky-header :element-id "root-sticky-header"}]
       [visual-viewport/observer]
       [:div#modal-container.sticky.top-0
        {:role  "complementary"
         :style {:z-index "8999"}} ; just below modal dialogs with their z-index of 9000
        [requirement/review-integration-session-modal tr]
        [whats-coming/whats-coming-modal tr]
        [conversation/application-conversation-overlay]
        [toast-notifications]]
       (when chat-enabled?
         [:<>
          (when (not side-panel-layout?)
            [chat-panel/chat-panel])
          [chat-panel/ticket-edit-modal tr]])
       [:div.flex.flex-row-reverse.flex-1.justify-end
        {:class (when full-screen-view? "min-h-0")}
        (when side-panel-layout?
          side-panels)
        [:div.flex.flex-1.items-stretch.min-w-0
         {:class (when full-screen-view? "min-h-0")}
         [:div.flex-1.min-w-0
          (merge (inert-props) {:class (when full-screen-view? "flex flex-col min-h-0")})
          ;; both flex elements are needed by conversation view
          ;; min-w-0 lets these flex items shrink below content width so wide
          ;; content (e.g. wizard form tables) scrolls internally instead of
          ;; overflowing the viewport on mobile.
          [main route-match logged-in?]]]]
       (when-not (or hide-footer? full-screen-view?)
         [footer tr route-match])
       [public-privacy-modal route-match]
       (when (or (= name :view/workspace-application)
                 (= name :view/workspace-execution))
         [conditions-comments/comments-overlay user])])
    (cc/loading-spinner {:with-overlay true})))
